import {Avatar, AvatarFallback, AvatarImage} from '@common-web/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@common-web/components/ui/sidebar';
import {toast} from '@common-web/hooks/use-toast';
import {useAuthStore} from '@common-web/stores/authStore';
import {LogOutIcon, MoreVerticalIcon, SettingsIcon} from 'lucide-react';
import {initialsFromFullName} from '@common-web/lib/nameUtils';
import {useNavigate} from 'react-router-dom';
import {ADP_URLS} from '@common/constants/adp';

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar: string | undefined;
  };
}) {
  const {isMobile} = useSidebar();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  // Signed in from the ADP app tile: logout returns to the ADP marketplace
  const adpSignIn = useAuthStore((state) => state.session?.adpSignIn === true);

  const handleLogout = async () => {
    try {
      await logout();
      if (adpSignIn) {
        window.location.assign(ADP_URLS.MARKETPLACE);
        return;
      }
      navigate('/login');
      toast({
        title: 'Success',
        description: 'Logged out successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to logout',
        variant: 'destructive',
      });
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg'>
                {user.avatar && (
                  <AvatarImage src={user.avatar} alt={user.name} className='grayscale' />
                )}
                <AvatarFallback className='rounded-lg text-muted-foreground'>
                  {initialsFromFullName(user.name)}
                </AvatarFallback>
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>{user.name}</span>
                <span className='truncate text-xs text-sidebar-foreground/70'>{user.email}</span>
              </div>
              <MoreVerticalIcon className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  {user.avatar && <AvatarImage src={user.avatar} alt={user.name} />}
                  <AvatarFallback className='rounded-lg'>
                    {initialsFromFullName(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-medium'>{user.name}</span>
                  <span className='truncate text-xs text-muted-foreground'>{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <SettingsIcon />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOutIcon />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
